import { supabase } from '../lib/supabase';

/**
 * Fetch all lines missing audio files with play details
 * @param {Object} filters - Optional filters for play, act, scene, character
 * @returns {Promise<Array>} Array of lines missing audio with details
 */
export const getMissingAudioLines = async (filters = {}) => {
  try {
    let query = supabase?.from('lines')?.select(`
        id,
        play_id,
        line_id,
        line_number,
        character,
        dialogue,
        scenes!inner(
          act_number,
          scene_number
        ),
        plays!inner(
          id,
          title,
          total_lines
        ),
        audio_recordings(
          id
        )
      `)?.is('audio_recordings.id', null)?.order('line_number', { ascending: true });

    // Apply filters if provided
    if (filters?.playId) {
      query = query?.eq('play_id', filters?.playId);
    }
    if (filters?.act) {
      query = query?.eq('scenes.act_number', filters?.act);
    }
    if (filters?.scene) {
      query = query?.eq('scenes.scene_number', filters?.scene);
    }
    if (filters?.character) {
      query = query?.ilike('character', `%${filters?.character}%`);
    }

    const { data, error } = await query;

    if (error) throw error;

    // Transform data to match expected format
    const transformedData = data?.map(line => ({
      line_id: line?.line_id,
      play_id: line?.play_id,
      act: line?.scenes?.act_number,
      scene: line?.scenes?.scene_number,
      speaker: line?.character,
      line_text: line?.dialogue,
      audio_url: null,
      plays: line?.plays
    })) || [];

    return transformedData;
  } catch (error) {
    console.error('Error fetching missing audio lines:', error);
    throw error;
  }
};

/**
 * Get summary statistics for missing audio files
 * @returns {Promise<Object>} Statistics object with counts
 */
export const getAudioStatistics = async () => {
  try {
    // Get total lines count
    const { count: totalLines, error: totalError } = await supabase?.from('lines')?.select('*', { count: 'exact', head: true });

    if (totalError) throw totalError;

    // Get lines with audio recordings
    const { data: linesWithAudio, error: audioError } = await supabase?.from('audio_recordings')?.select('line_id');

    if (audioError) throw audioError;

    const uniqueLinesWithAudio = new Set(linesWithAudio?.map(record => record?.line_id));
    const completedAudio = uniqueLinesWithAudio?.size || 0;
    const missingAudio = (totalLines || 0) - completedAudio;

    // Get play-wise breakdown
    const { data: allLines, error: linesError } = await supabase?.from('lines')?.select(`
        id,
        play_id,
        plays!inner(title),
        audio_recordings(id)
      `);

    if (linesError) throw linesError;

    const playStats = allLines?.reduce((acc, line) => {
      const playTitle = line?.plays?.title;
      const hasAudio = line?.audio_recordings?.length > 0;
      
      if (!hasAudio) {
        acc[playTitle] = (acc?.[playTitle] || 0) + 1;
      }
      return acc;
    }, {});

    return {
      totalLines: totalLines || 0,
      missingAudio: missingAudio,
      completedAudio: completedAudio,
      completionPercentage: totalLines ? Math.round((completedAudio / totalLines) * 100) : 0,
      playBreakdown: playStats || {}
    };
  } catch (error) {
    console.error('Error fetching audio statistics:', error);
    throw error;
  }
};

/**
 * Get unique acts and scenes for a specific play
 * @param {string} playId - Play ID
 * @returns {Promise<Object>} Object with acts and scenes arrays
 */
export const getPlayStructure = async (playId) => {
  try {
    const { data, error } = await supabase?.from('lines')?.select(`
        scenes!inner(
          act_number,
          scene_number
        ),
        audio_recordings(id)
      `)?.eq('play_id', playId);

    if (error) throw error;

    // Filter lines without audio
    const linesWithoutAudio = data?.filter(line => !line?.audio_recordings || line?.audio_recordings?.length === 0);

    const acts = [...new Set(linesWithoutAudio?.map(line => line?.scenes?.act_number))]?.sort((a, b) => a - b);
    const scenes = [...new Set(linesWithoutAudio?.map(line => line?.scenes?.scene_number))]?.sort((a, b) => a - b);

    return { acts, scenes };
  } catch (error) {
    console.error('Error fetching play structure:', error);
    throw error;
  }
};

/**
 * Get unique characters for filtering
 * @param {string} playId - Optional play ID to filter characters
 * @returns {Promise<Array>} Array of unique character names
 */
export const getCharactersList = async (playId = null) => {
  try {
    let query = supabase?.from('lines')?.select(`
        character,
        audio_recordings(id)
      `);

    if (playId) {
      query = query?.eq('play_id', playId);
    }

    const { data, error } = await query;

    if (error) throw error;

    // Filter lines without audio
    const linesWithoutAudio = data?.filter(line => !line?.audio_recordings || line?.audio_recordings?.length === 0);

    const characters = [...new Set(linesWithoutAudio?.map(line => line?.character)?.filter(Boolean))]?.sort();
    return characters;
  } catch (error) {
    console.error('Error fetching characters list:', error);
    throw error;
  }
};

/**
 * Export missing audio lines to CSV format
 * @param {Array} lines - Array of line objects to export
 * @returns {string} CSV formatted string
 */
export const exportToCSV = (lines) => {
  const headers = ['Line ID', 'Play', 'Act', 'Scene', 'Character', 'Line Text'];
  const rows = lines?.map(line => [
    line?.line_id,
    line?.plays?.title || '',
    line?.act,
    line?.scene,
    line?.speaker,
    `"${line?.line_text?.replace(/"/g, '""') || ''}"` // Escape quotes in CSV
  ]);

  const csv = [
    headers?.join(','),
    ...rows?.map(row => row?.join(','))
  ]?.join('\n');

  return csv;
};

/**
 * Download CSV file
 * @param {string} csvContent - CSV formatted string
 * @param {string} filename - Name for the downloaded file
 */
export const downloadCSV = (csvContent, filename = 'missing_audio_lines.csv') => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link?.setAttribute('href', url);
  link?.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body?.appendChild(link);
  link?.click();
  document.body?.removeChild(link);
};

// ========================================
// 🎵 AUDIO STORAGE FUNCTIONS
// ========================================

/**
 * Upload audio file to Supabase Storage
 * @param {File} file - Audio file to upload
 * @param {string} lineId - Line ID for organizing files
 * @returns {Promise<Object>} Upload result with file path
 */
export const uploadAudioFile = async (file, lineId) => {
  try {
    // Validate file type
    const validTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-m4a', 'audio/m4a'];
    if (!validTypes?.includes(file?.type)) {
      throw new Error('Invalid file type. Please upload MP3, WAV, or M4A files only.');
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file?.size > maxSize) {
      throw new Error('File size exceeds 10MB limit. Please upload a smaller file.');
    }

    // Create unique file path
    const timestamp = Date.now();
    const fileExt = file?.name?.split('.')?.pop();
    const filePath = `${lineId}/${timestamp}-${file?.name}`;

    // Upload to storage
    const { data, error } = await supabase?.storage?.from('audio-files')?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) throw error;

    // Get signed URL for the uploaded file
    const { data: urlData, error: urlError } = await supabase?.storage?.from('audio-files')?.createSignedUrl(data?.path, 31536000); // 1 year expiry

    if (urlError) throw urlError;

    return {
      path: data?.path,
      signedUrl: urlData?.signedUrl,
      fullPath: data?.fullPath
    };
  } catch (error) {
    console.error('Error uploading audio file:', error);
    throw error;
  }
};

/**
 * Get signed URL for audio file playback
 * @param {string} filePath - Storage file path
 * @param {number} expirySeconds - URL expiry time in seconds (default 1 hour)
 * @returns {Promise<string>} Signed URL for audio playback
 */
export const getAudioFileUrl = async (filePath, expirySeconds = 3600) => {
  try {
    if (!filePath) return null;
    const { data, error } = await supabase?.storage?.from('audio-files')?.createSignedUrl(filePath, expirySeconds);

    if (error) {
      // Gracefully handle missing files instead of throwing
      if (error?.message?.includes('Object not found') || error?.statusCode === 404 || error?.status === 404) {
        return null;
      }
      throw error;
    }

    return data?.signedUrl;
  } catch (error) {
    console.error('Error getting audio file URL:', error);
    throw error;
  }
};

/**
 * Delete audio file from storage
 * @param {string} filePath - Storage file path to delete
 * @returns {Promise<void>}
 */
export const deleteAudioFile = async (filePath) => {
  try {
    const { error } = await supabase?.storage?.from('audio-files')?.remove([filePath]);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting audio file:', error);
    throw error;
  }
};

/**
 * Create audio recording entry in database
 * @param {string} lineId - Line UUID
 * @param {string} audioPath - Storage path for the audio file
 * @param {number} durationSeconds - Audio duration in seconds
 * @param {string} userId - User ID who uploaded the audio
 * @returns {Promise<Object>} Created audio recording
 */
export const createAudioRecording = async (lineId, audioPath, durationSeconds = null, userId = null) => {
  try {
    const { data, error } = await supabase
      ?.from('audio_recordings')
      ?.insert({
        line_id: lineId,
        audio_url: audioPath,
        duration_seconds: durationSeconds,
        user_id: userId
      })
      ?.select()
      ?.single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error creating audio recording:', error);
    throw error;
  }
};

/**
 * Complete audio upload workflow: upload file and update database
 * @param {File} file - Audio file to upload
 * @param {string} lineId - Line UUID (from lines table)
 * @param {string} userId - User ID who uploaded the audio
 * @returns {Promise<Object>} Complete upload result
 */
export const completeAudioUpload = async (file, lineId, userId = null) => {
  try {
    // Step 1: Upload file to storage
    const uploadResult = await uploadAudioFile(file, lineId);

    // Step 2: Create audio recording entry in database
    const audioRecording = await createAudioRecording(
      lineId,
      uploadResult?.path,
      null, // duration can be calculated client-side if needed
      userId
    );

    return {
      success: true,
      uploadResult,
      audioRecording,
      message: 'Audio file uploaded successfully'
    };
  } catch (error) {
    console.error('Error completing audio upload:', error);
    throw error;
  }
};

/**
 * List all audio files for a specific line
 * @param {string} lineId - Line ID to fetch audio files for
 * @returns {Promise<Array>} Array of audio file objects
 */
export const listLineAudioFiles = async (lineId) => {
  try {
    const { data, error } = await supabase?.storage?.from('audio-files')?.list(lineId, {
        sortBy: { column: 'created_at', order: 'desc' }
      });

    if (error) throw error;

    // Get signed URLs for each file
    const filesWithUrls = await Promise.all(
      data?.map(async (file) => {
        const filePath = `${lineId}/${file?.name}`;
        const signedUrl = await getAudioFileUrl(filePath);
        return {
          ...file,
          filePath,
          signedUrl
        };
      })
    );

    return filesWithUrls;
  } catch (error) {
    console.error('Error listing line audio files:', error);
    throw error;
  }
};

/**
 * Get audio recordings from database for a specific line
 * @param {string} lineId - Line UUID
 * @returns {Promise<Array>} Array of audio recording objects with signed URLs
 */
export const getLineAudioRecordings = async (lineId) => {
  try {
    const { data, error } = await supabase
      ?.from('audio_recordings')
      ?.select('*')
      ?.eq('line_id', lineId)
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    // Get signed URLs for each recording
    const recordingsWithUrls = await Promise.all(
      (data || [])?.map(async (recording) => {
        try {
          const signedUrl = await getAudioFileUrl(recording?.audio_url);
          return {
            ...recording,
            signedUrl
          };
        } catch (urlError) {
          console.error('Error getting signed URL for recording:', urlError);
          return recording;
        }
      })
    );

    return recordingsWithUrls;
  } catch (error) {
    console.error('Error getting line audio recordings:', error);
    throw error;
  }
};

/**
 * Delete audio recording from database and storage
 * @param {string} recordingId - Audio recording UUID
 * @returns {Promise<void>}
 */
export const deleteAudioRecording = async (recordingId) => {
  try {
    // First, get the recording to find the storage path
    const { data: recording, error: fetchError } = await supabase
      ?.from('audio_recordings')
      ?.select('audio_url')
      ?.eq('id', recordingId)
      ?.single();

    if (fetchError) throw fetchError;

    // Delete from storage
    if (recording?.audio_url) {
      await deleteAudioFile(recording?.audio_url);
    }

    // Delete from database
    const { error: deleteError } = await supabase
      ?.from('audio_recordings')
      ?.delete()
      ?.eq('id', recordingId);

    if (deleteError) throw deleteError;
  } catch (error) {
    console.error('Error deleting audio recording:', error);
    throw error;
  }
};

/**
 * Update audio recording metadata
 * @param {string} recordingId - Audio recording ID
 * @param {Object} updates - Fields to update (userId, durationSeconds, etc.)
 * @returns {Promise<Object>} Updated recording
 */
export const updateAudioRecording = async (recordingId, updates = {}) => {
  try {
    const updateData = {};

    if (updates?.userId) {
      updateData.user_id = updates?.userId;
    }
    if (updates?.durationSeconds !== undefined) {
      updateData.duration_seconds = updates?.durationSeconds;
    }

    const { data, error } = await supabase
      ?.from('audio_recordings')
      ?.update(updateData)
      ?.eq('id', recordingId)
      ?.select()
      ?.single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('Error updating audio recording:', error);
    throw error;
  }
};

/**
 * Update line with audio URL in database
 * @param {string} lineId - Line ID to update
 * @param {string} audioPath - Storage path for the audio file
 * @returns {Promise<void>}
 */
export const updateLineAudioUrl = async (lineId, audioPath) => {
  try {
    const { error } = await supabase
      ?.from('lines')
      ?.update({ audio_url: audioPath })
      ?.eq('id', lineId);

    if (error) throw error;
  } catch (error) {
    console.error('Error updating line audio URL:', error);
    throw error;
  }
};